import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  VStack,
  HStack,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import { 
  checkIfMigrationNeeded, 
  migrateAllData, 
  clearLocalStorageAfterMigration,
  restoreFromBackup
} from '../utils/migrationUtils';

/**
 * MigrationHelper Component
 * 
 * This component helps users migrate their data from localStorage
 * to the new database-backed on-the-fly card generation system.
 */
const MigrationHelper = ({ onMigrationComplete }) => {
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const [migrationInProgress, setMigrationInProgress] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [migrationError, setMigrationError] = useState(null);
  const [migrationStats, setMigrationStats] = useState(null);
  const [progress, setProgress] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Check if migration is needed
  useEffect(() => {
    const needsMigration = checkIfMigrationNeeded();
    setMigrationNeeded(needsMigration);
    
    if (needsMigration) {
      onOpen();
    }
  }, [onOpen]);

  // Handle migration process
  const handleMigration = async () => {
    try {
      setMigrationInProgress(true);
      setMigrationError(null);
      setProgress(10);
      
      // Start migration
      const result = await migrateAllData();
      setProgress(90);
      
      // Set migration stats
      setMigrationStats(result);
      
      // Clear localStorage after successful migration
      clearLocalStorageAfterMigration(true);
      setProgress(100);
      
      // Set migration complete
      setMigrationComplete(true);
      setMigrationInProgress(false);
      
      // Show success toast
      toast({
        title: 'Migration Complete',
        description: 'Your scratch card data has been successfully migrated to the new system.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      
      // Call onMigrationComplete callback if provided
      if (onMigrationComplete) {
        onMigrationComplete(result);
      }
    } catch (error) {
      console.error('Migration error:', error);
      setMigrationError(error.message || 'An error occurred during migration');
      setMigrationInProgress(false);
      
      // Show error toast
      toast({
        title: 'Migration Failed',
        description: error.message || 'An error occurred during migration',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Handle restore from backup
  const handleRestore = () => {
    try {
      const restored = restoreFromBackup();
      
      if (restored) {
        toast({
          title: 'Restore Complete',
          description: 'Your data has been restored from backup.',
          status: 'success',
          duration: 5000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Restore Failed',
          description: 'No backup data found or restore failed.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Restore error:', error);
      toast({
        title: 'Restore Failed',
        description: error.message || 'An error occurred during restore',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // If no migration is needed, don't render anything
  if (!migrationNeeded && !migrationComplete) {
    return null;
  }

  return (
    <>
      {/* Migration Alert */}
      {migrationNeeded && !migrationComplete && (
        <Alert 
          status="info" 
          variant="solid" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          textAlign="center" 
          p={4}
          mb={4}
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Data Migration Available
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            We've updated our scratch card system. Migrate your existing cards to the new system for better performance.
          </AlertDescription>
          <Button 
            colorScheme="blue" 
            mt={4} 
            onClick={onOpen}
            isLoading={migrationInProgress}
            loadingText="Migrating..."
          >
            Migrate Now
          </Button>
        </Alert>
      )}

      {/* Migration Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Scratch Card Data Migration</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!migrationComplete ? (
              <VStack spacing={4} align="stretch">
                <Text>
                  We've updated our scratch card system to improve performance and add new features.
                  This migration will move your existing scratch card data to the new system.
                </Text>
                
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>What will be migrated:</AlertTitle>
                    <AlertDescription>
                      • Your scratch card games<br />
                      • Your cards and game progress<br />
                      • Your lottery tickets<br />
                      • Any pending lottery draws
                    </AlertDescription>
                  </Box>
                </Alert>
                
                {migrationInProgress && (
                  <Box>
                    <Text mb={2}>Migration in progress...</Text>
                    <Progress 
                      value={progress} 
                      size="sm" 
                      colorScheme="blue" 
                      hasStripe 
                      isAnimated 
                    />
                  </Box>
                )}
                
                {migrationError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Migration Failed</AlertTitle>
                      <AlertDescription>{migrationError}</AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Migration Complete!</AlertTitle>
                    <AlertDescription>
                      Your data has been successfully migrated to the new system.
                    </AlertDescription>
                  </Box>
                </Alert>
                
                {migrationStats && (
                  <Box borderWidth="1px" borderRadius="md" p={4}>
                    <Heading size="sm" mb={2}>Migration Summary</Heading>
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text>Games:</Text>
                        <Badge colorScheme="green">{migrationStats.games || 0} migrated</Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>Cards:</Text>
                        <Badge colorScheme="green">{migrationStats.cards || 0} migrated</Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>Lottery Tickets:</Text>
                        <Badge colorScheme="green">{migrationStats.tickets || 0} migrated</Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>Lottery Draws:</Text>
                        <Badge colorScheme="green">{migrationStats.draws || 0} migrated</Badge>
                      </HStack>
                    </VStack>
                  </Box>
                )}
                
                <Text fontSize="sm" color="gray.500">
                  A backup of your data has been saved. If you encounter any issues,
                  you can restore from the backup.
                </Text>
              </VStack>
            )}
          </ModalBody>
          
          <ModalFooter>
            {!migrationComplete ? (
              <>
                <Button 
                  variant="ghost" 
                  mr={3} 
                  onClick={onClose}
                  isDisabled={migrationInProgress}
                >
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={handleMigration}
                  isLoading={migrationInProgress}
                  loadingText="Migrating..."
                  isDisabled={migrationInProgress}
                >
                  Start Migration
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  mr={3} 
                  onClick={handleRestore}
                >
                  Restore from Backup
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={onClose}
                >
                  Done
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MigrationHelper;
